import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAdminPendingCount } from "@/admin/AdminApplications";
import {
  LayoutDashboard,
  Building2,
  Store,
  MessageSquareWarning,
  Megaphone,
  Star,
  ScrollText,
  ListChecks,
  MessageSquare,
  TrendingUp,
  Mail,
  Activity,
  ArrowDownUp,
  Languages as LanguagesIcon,
  Users as UsersIcon,
  Settings as SettingsIcon,
  LifeBuoy,
  LogOut,
  Lock,
  ShieldCheck,
  Menu,
  X,
  MapPin,
  Trash2,
  Eye,
  FolderTree,
  ImagePlus,
  Bike,
  Images,
  ClipboardCheck,
  BellRing,
  Briefcase,
} from "lucide-react";
import { api, getMe, logout } from "../lib/api";
import { LogoMark } from "../components/Logo";

const NAV = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Overview", section: 1, enabled: true },
  { to: "/admin/businesses", icon: Building2, label: "Businesses", section: 2, enabled: true },
  { to: "/admin/categories", icon: FolderTree, label: "Categories", section: 2, enabled: true },
  { to: "/admin/ride-mode-backgrounds", icon: ImagePlus, label: "Ride Backgrounds", section: 2, enabled: true },
  { to: "/admin/transport", icon: Bike, label: "Transportation", section: 2, enabled: true },
  { to: "/admin/media", icon: Images, label: "Media", section: 2, enabled: true },
  { to: "/admin/applications", icon: ClipboardCheck, label: "Applications", section: 2, enabled: true, showPending: true },
  { to: "/admin/jobs", icon: Briefcase, label: "Jobs & Bids", section: 2, enabled: true },
  { to: "/admin/vendor-categories", icon: Briefcase, label: "Categories", section: 2, enabled: true },
  { to: "/admin/push", icon: BellRing, label: "Push Notifications", section: 2, enabled: true },
  { to: "/admin/vendors", icon: Store, label: "Vendors", section: 3, enabled: true },
  { to: "/admin/reviews", icon: MessageSquareWarning, label: "Reviews", section: 4, enabled: true },
  { to: "/admin/banners", icon: Megaphone, label: "Banners", section: 5, enabled: true },
  { to: "/admin/updates", icon: Megaphone, label: "Updates", section: 5, enabled: true },
  { to: "/admin/tips", icon: ListChecks, label: "Tips", section: 13, enabled: true },
  { to: "/admin/exchange-rate", icon: TrendingUp, label: "Exchange Rate", section: 14, enabled: true },
  { to: "/admin/whatsapp", icon: MessageSquare, label: "WhatsApp Services", section: 15, enabled: true },
  { to: "/admin/whatsapp-activity", icon: Activity, label: "WhatsApp Activity", section: 16, enabled: true },
  { to: "/admin/email", icon: Mail, label: "Email Configuration", section: 17, enabled: true },
  { to: "/admin/featured", icon: Star, label: "Featured", section: 6, enabled: true },
  { to: "/admin/audit", icon: ScrollText, label: "Audit log", section: 7, enabled: true },
  { to: "/admin/data", icon: ArrowDownUp, label: "Import / Export", section: 8, enabled: true },
  { to: "/admin/translations", icon: LanguagesIcon, label: "Translations", section: 9, enabled: true },
  { to: "/admin/users", icon: UsersIcon, label: "Users", section: 10, enabled: true },
  { to: "/admin/support", icon: LifeBuoy, label: "Support Tickets", section: 18, enabled: true },
  { to: "/admin/shared-locations", icon: MapPin, label: "Shared Locations", section: 19, enabled: true },
  { to: "/admin/promotion-templates", icon: MessageSquare, label: "Rec Templates", section: 20, enabled: true },
  { to: "/admin/recommendations", icon: Megaphone, label: "Rec Log", section: 20, enabled: true },
  { to: "/admin/vendor-interactions", icon: Activity, label: "Vendor Outreach", section: 20, enabled: true },
  { to: "/admin/settings", icon: SettingsIcon, label: "Settings", section: 11, enabled: true },
  { to: "/admin/data-cleanup", icon: Trash2, label: "Data Cleanup", section: 21, enabled: true },
  { to: "/admin/audience-settings", icon: Eye, label: "Audience Settings", section: 22, enabled: true },
  { to: "/admin/whatsapp-cloud", icon: MessageSquare, label: "WhatsApp Cloud", section: 23, enabled: true },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [state, setState] = useState("loading");
  const [navOpen, setNavOpen] = useState(false);
  // iter195 — Pending applications badge count, polled every 15s.
  const pendingCount = useAdminPendingCount();

  useEffect(() => {
    (async () => {
      const me = await getMe();
      if (!me) {
        navigate("/?next=/admin");
        return;
      }
      setUser(me);
      try {
        await api.get("/admin/me");
        setState("ok");
      } catch {
        setState("denied");
      }
    })();
  }, [navigate]);

  // Auto-close mobile nav when route changes
  useEffect(() => { setNavOpen(false); }, [location.pathname]);
  // Lock body scroll when drawer open
  useEffect(() => {
    if (navOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [navOpen]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
    window.location.reload();
  };

  if (state === "loading") {
    return (
      <div className="min-h-screen grid place-items-center bg-background" data-testid="admin-loading">
        <div className="text-center">
          <div className="eyebrow">Habano Admin</div>
          <div className="font-display text-2xl mt-2 text-gold">Authorizing…</div>
        </div>
      </div>
    );
  }

  if (state === "denied") {
    return (
      <main className="min-h-screen grid place-items-center px-6 bg-background" data-testid="admin-denied">
        <div className="max-w-md lux-card p-8 text-center">
          <span className="mx-auto w-12 h-12 grid place-items-center rounded-full border border-gold-strong">
            <Lock className="w-5 h-5 text-gold" strokeWidth={1.5} />
          </span>
          <div className="eyebrow mt-5">Access denied</div>
          <h1 className="font-display text-3xl mt-2 text-foreground">Members only</h1>
          <p className="text-sm text-foreground/65 mt-3">
            You're signed in as <span className="font-mono text-gold">{user?.email}</span>, but this account
            isn't on the admin list. Ask the system owner to add your email to{" "}
            <span className="font-mono">ADMIN_EMAILS</span>.
          </p>
          <div className="mt-6 flex gap-2 justify-center">
            <Link to="/" data-testid="admin-back-home" className="btn-outline py-2 px-4 text-xs">
              Go home
            </Link>
            <button onClick={handleLogout} data-testid="admin-logout-deny" className="btn-gold py-2 px-4 text-xs">
              Log out
            </button>
          </div>
        </div>
      </main>
    );
  }

  const sidebar = (mobile) => (
    <>
      <Link
        to="/admin"
        className="flex items-center gap-2 px-6 py-6 border-b border-gold-soft"
        data-testid={mobile ? "admin-sidebar-logo-mobile" : "admin-sidebar-logo"}
        onClick={mobile ? () => setNavOpen(false) : undefined}
      >
        <LogoMark />
      </Link>

      <div className="px-6 py-3 eyebrow">Console</div>

      <nav className="flex-1 overflow-y-auto px-3 pb-4">
        {NAV.map((item) => {
          const Icon = item.icon;
          const cls = "flex items-center gap-3 px-3 py-3 min-h-[48px] text-base md:text-sm rounded-[10px] transition-colors";
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `${cls} ${
                  isActive
                    ? "bg-[rgba(232,184,75,0.10)] text-gold border border-gold-soft"
                    : "text-foreground/75 hover:bg-white/5 hover:text-foreground border border-transparent"
                }`
              }
            >
              <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
              <span className="flex-1">{item.label}</span>
              {item.showPending && pendingCount > 0 && (
                <span
                  data-testid="admin-nav-applications-badge"
                  className="grid place-items-center rounded-full"
                  style={{
                    minWidth: 20, height: 20, padding: "0 6px",
                    background: "#E8B84B", color: "#1a0f06",
                    fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 700,
                  }}
                >{pendingCount}</span>
              )}
              <span className="text-[10px] font-mono opacity-60">§{item.section}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t border-gold-soft p-4 space-y-3 text-xs">
        <div className="flex items-center gap-3">
          {user?.profile_photo ? (
            <img src={user.profile_photo} alt="" className="w-8 h-8 rounded-full object-cover ring-1 ring-[rgba(232,184,75,0.4)]" />
          ) : (
            <span className="w-8 h-8 rounded-full grid place-items-center bg-surface text-gold text-[10px] font-medium ring-1 ring-[rgba(232,184,75,0.4)]">
              {(user?.display_name || user?.email || "?").slice(0, 2).toUpperCase()}
            </span>
          )}
          <div className="min-w-0">
            <div className="font-medium truncate text-foreground">{user?.display_name}</div>
            <div className="text-foreground/50 truncate">{user?.email}</div>
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid={mobile ? "admin-logout-mobile" : "admin-logout"}
          className="btn-outline w-full py-2 text-xs min-h-[44px]"
        >
          <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} /> Log out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen md:flex bg-background" data-testid="admin-shell">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-72 shrink-0 border-r border-gold-soft bg-havana flex-col sticky top-0 h-screen">
        {sidebar(false)}
      </aside>

      {/* Mobile drawer */}
      {navOpen && (
        <>
          <button
            aria-label="Close menu"
            data-testid="admin-mobile-overlay"
            onClick={() => setNavOpen(false)}
            className="md:hidden fixed inset-0 bg-black/85 z-40 backdrop-blur-sm"
          />
          <aside
            data-testid="admin-mobile-sidebar"
            className="md:hidden fixed inset-y-0 left-0 z-50 w-[85%] max-w-xs bg-havana border-r border-gold-soft flex flex-col animate-in slide-in-from-left duration-300"
          >
            <button
              data-testid="admin-mobile-close"
              onClick={() => setNavOpen(false)}
              aria-label="Close menu"
              className="absolute top-4 right-4 w-10 h-10 grid place-items-center rounded-full border border-gold-soft text-foreground/70 hover:text-gold"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
            {sidebar(true)}
          </aside>
        </>
      )}

      <main className="flex-1 min-w-0">
        <header className="h-14 sm:h-16 border-b border-gold-soft bg-background/80 backdrop-blur-sm flex items-center justify-between px-4 sm:px-8 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setNavOpen(true)}
              data-testid="admin-mobile-menu-trigger"
              aria-label="Open menu"
              className="md:hidden w-10 h-10 grid place-items-center rounded-full border border-gold-soft text-foreground/70 hover:text-gold"
            >
              <Menu className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <ShieldCheck className="hidden sm:block w-4 h-4 text-gold" strokeWidth={1.5} />
            <div className="eyebrow text-[10px] sm:text-[11px]">
              <span className="md:hidden">Admin</span>
              <span className="hidden md:inline">Admin · dashboard.habano.app</span>
            </div>
          </div>
          <Link to="/" className="text-xs text-foreground/55 hover:text-gold transition-colors" data-testid="admin-back-to-app">
            ← Back to app
          </Link>
        </header>
        <div className="p-4 sm:p-8 md:p-12 max-w-7xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
